// src/pages/finance-portal/expenses/ExpenseForm.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, TextField, MenuItem, Switch, FormControlLabel,
    Dialog, DialogTitle, DialogContent, DialogActions, Alert, Grid,
    Autocomplete, CircularProgress, Chip, InputAdornment,
} from '@mui/material';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const PAYMENT_METHODS = [
    { value: 'CASH', label: 'Cash' },
    { value: 'PERSONAL_CARD', label: 'Personal Card' },
    { value: 'CORPORATE_CARD', label: 'Corporate Card' },
    { value: 'ACH', label: 'ACH' },
];

const defaultForm = {
    expense_date: new Date().toISOString().split('T')[0],
    merchant_name: '',
    description: '',
    category_id: '',
    amount: '',
    tax_amount: '',
    tip_amount: '',
    payment_method: 'PERSONAL_CARD',
    is_billable: false,
    is_reimbursable: true,
    is_mileage: false,
    mileage_distance: '',
    mileage_rate: '0.655',
    project_id: '',
    cost_center: '',
    gl_account_id: null,
    tags: [],
};

export default function ExpenseForm({ open, onClose, onSaved, expense }) {
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});

    // Categories
    const [categories, setCategories] = useState([]);

    // GL Accounts
    const [glAccounts, setGlAccounts] = useState([]);
    const [glLoading, setGlLoading] = useState(false);

    // Tags input
    const [tagInput, setTagInput] = useState('');

    // Load categories
    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const res = await axiosInstance.get('/api/finance/expense-categories');
                if (res.data.success) setCategories(res.data.data || []);
            } catch (err) {
                console.error('Failed to load categories', err);
            }
        })();
    }, [open]);

    // GL accounts - fetch once on open
    useEffect(() => {
        if (!open) return;
        setGlLoading(true);
        (async () => {
            try {
                const res = await axiosInstance.get('/api/finance/coa', { params: { flat: true } });
                if (res.data.success) {
                    setGlAccounts(res.data.data || []);
                }
            } catch (err) {
                console.error('Failed to load GL accounts', err);
            } finally {
                setGlLoading(false);
            }
        })();
    }, [open]);

    // Populate form when editing
    useEffect(() => {
        if (expense) {
            setForm({
                expense_date: expense.expense_date || defaultForm.expense_date,
                merchant_name: expense.merchant_name || '',
                description: expense.description || '',
                category_id: expense.category_id || '',
                amount: expense.amount || '',
                tax_amount: expense.tax_amount || '',
                tip_amount: expense.tip_amount || '',
                payment_method: expense.payment_method || 'PERSONAL_CARD',
                is_billable: expense.is_billable || false,
                is_reimbursable: expense.is_reimbursable !== undefined ? expense.is_reimbursable : true,
                is_mileage: expense.is_mileage || false,
                mileage_distance: expense.mileage_distance || '',
                mileage_rate: expense.mileage_rate || '0.655',
                project_id: expense.project_id || '',
                cost_center: expense.cost_center || '',
                gl_account_id: expense.gl_account_id || null,
                tags: expense.tags || [],
            });
        } else {
            setForm(defaultForm);
        }
        setError(null);
    }, [expense, open]);

    const handleChange = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleToggle = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.checked }));
    };

    // Auto-calc amount for mileage
    const computedAmount = form.is_mileage && form.mileage_distance && form.mileage_rate
        ? (parseFloat(form.mileage_distance) * parseFloat(form.mileage_rate)).toFixed(2)
        : form.amount;

    // Total = amount + tax + tip
    const totalAmount = (
        parseFloat(computedAmount || 0) +
        parseFloat(form.tax_amount || 0) +
        parseFloat(form.tip_amount || 0)
    ).toFixed(2);

    const handleAddTag = (e) => {
        if (e.key === 'Enter' && tagInput.trim()) {
            e.preventDefault();
            if (!form.tags.includes(tagInput.trim())) {
                setForm(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
            }
            setTagInput('');
        }
    };

    const handleRemoveTag = (tag) => {
        setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
    };

    const handleSave = async (submitAfterSave = false) => {
        setSaving(true);
        setError(null);
        try {
            const payload = {
                ...form,
                amount: form.is_mileage ? computedAmount : form.amount,
                total_amount: totalAmount,
            };

            let res;
            if (expense?.id) {
                res = await axiosInstance.put(`/api/finance/expenses/${expense.id}`, payload);
            } else {
                res = await axiosInstance.post('/api/finance/expenses', payload);
            }

            if (res.data.success) {
                if (submitAfterSave && res.data.data?.id) {
                    await axiosInstance.post(`/api/finance/expenses/${res.data.data.id}/submit`, {});
                }
                onSaved();
                onClose();
            } else {
                setError(res.data.message || 'Failed to save expense');
            }
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            const msg = err.response?.data?.message || 'Failed to save expense';
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                setError(msg);
            } else {
                setError(msg);
            }
        } finally {
            setSaving(false);
        }
    };

    const fieldSx = { fontFamily, fontSize: '0.85rem', borderRadius: '10px' };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: '16px' } }}
        >
            <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.2rem', pb: 1 }}>
                {expense ? 'Edit Expense' : 'Add Expense'}
            </DialogTitle>
            <DialogContent dividers sx={{ pt: 2 }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Grid container spacing={2}>
                    {/* Row 1: Date, Merchant */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth size="small" type="date" label="Expense Date"
                            value={form.expense_date}
                            onChange={handleChange('expense_date')}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ sx: fieldSx }}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <TextField
                            fullWidth size="small" label="Merchant Name"
                            value={form.merchant_name}
                            onChange={handleChange('merchant_name')}
                            InputProps={{ sx: fieldSx }}
                        />
                    </Grid>

                    {/* Row 2: Description */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth size="small" label="Description"
                            value={form.description}
                            onChange={handleChange('description')}
                            multiline rows={2}
                            InputProps={{ sx: fieldSx }}
                        />
                    </Grid>

                    {/* Row 3: Category, Payment Method */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth size="small" select label="Category"
                            value={form.category_id}
                            onChange={handleChange('category_id')}
                            InputProps={{ sx: fieldSx }}
                        >
                            <MenuItem value="">-- Select --</MenuItem>
                            {categories.map(cat => (
                                <MenuItem key={cat.id} value={cat.id} sx={{ fontFamily }}>{cat.name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth size="small" select label="Payment Method"
                            value={form.payment_method}
                            onChange={handleChange('payment_method')}
                            InputProps={{ sx: fieldSx }}
                        >
                            {PAYMENT_METHODS.map(pm => (
                                <MenuItem key={pm.value} value={pm.value} sx={{ fontFamily }}>{pm.label}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {/* Mileage toggle */}
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Switch checked={form.is_mileage} onChange={handleToggle('is_mileage')} color="success" />}
                            label={<Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600 }}>Mileage Expense</Typography>}
                        />
                    </Grid>

                    {/* Mileage fields or Amount */}
                    {form.is_mileage ? (
                        <>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth size="small" label="Distance (miles)"
                                    type="number"
                                    value={form.mileage_distance}
                                    onChange={handleChange('mileage_distance')}
                                    InputProps={{ sx: fieldSx }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth size="small" label="Rate ($/mile)"
                                    type="number"
                                    value={form.mileage_rate}
                                    onChange={handleChange('mileage_rate')}
                                    InputProps={{ sx: fieldSx }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth size="small" label="Amount (auto-calculated)"
                                    value={computedAmount || '0.00'}
                                    InputProps={{ sx: fieldSx, startAdornment: <InputAdornment position="start">$</InputAdornment>, readOnly: true }}
                                    disabled
                                />
                            </Grid>
                        </>
                    ) : (
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth size="small" label="Amount"
                                type="number"
                                value={form.amount}
                                onChange={handleChange('amount')}
                                InputProps={{ sx: fieldSx, startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                required
                            />
                        </Grid>
                    )}

                    {/* Tax, Tip, Total */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth size="small" label="Tax Amount"
                            type="number"
                            value={form.tax_amount}
                            onChange={handleChange('tax_amount')}
                            InputProps={{ sx: fieldSx, startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth size="small" label="Tip Amount"
                            type="number"
                            value={form.tip_amount}
                            onChange={handleChange('tip_amount')}
                            InputProps={{ sx: fieldSx, startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                    </Grid>
                    {(!form.is_mileage) && <Grid item xs={12} sm={4} />}
                    <Grid item xs={12} sm={4}>
                        <Box sx={{ p: 1.5, bgcolor: '#f0fdf4', borderRadius: '10px', textAlign: 'center' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Total</Typography>
                            <Typography sx={{ fontFamily, fontSize: '1.2rem', fontWeight: 800, color: ACCENT }}>${totalAmount}</Typography>
                        </Box>
                    </Grid>

                    {/* Toggles */}
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={<Switch checked={form.is_billable} onChange={handleToggle('is_billable')} color="success" />}
                            label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>Billable</Typography>}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControlLabel
                            control={<Switch checked={form.is_reimbursable} onChange={handleToggle('is_reimbursable')} color="success" />}
                            label={<Typography sx={{ fontFamily, fontSize: '0.85rem' }}>Reimbursable</Typography>}
                        />
                    </Grid>

                    {/* Project, Cost Center */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth size="small" label="Project ID"
                            value={form.project_id}
                            onChange={handleChange('project_id')}
                            InputProps={{ sx: fieldSx }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth size="small" label="Cost Center"
                            value={form.cost_center}
                            onChange={handleChange('cost_center')}
                            InputProps={{ sx: fieldSx }}
                        />
                    </Grid>

                    {/* GL Account */}
                    <Grid item xs={12}>
                        <Autocomplete
                            size="small"
                            options={glAccounts}
                            getOptionLabel={(opt) => opt ? `${opt.account_number} - ${opt.name}` : ''}
                            value={glAccounts.find(a => a.id === form.gl_account_id) || null}
                            onChange={(_, val) => setForm(prev => ({ ...prev, gl_account_id: val?.id || null }))}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                            loading={glLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="GL Account Override"
                                    placeholder="Select GL Account..."
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: fieldSx,
                                        endAdornment: (
                                            <>
                                                {glLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    {/* Tags */}
                    <Grid item xs={12}>
                        <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#64748b', mb: 0.5 }}>Tags</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {form.tags.map(tag => (
                                <Chip
                                    key={tag}
                                    label={tag}
                                    size="small"
                                    onDelete={() => handleRemoveTag(tag)}
                                    sx={{ fontFamily, fontSize: '0.75rem' }}
                                />
                            ))}
                        </Box>
                        <TextField
                            fullWidth size="small"
                            placeholder="Type a tag and press Enter"
                            value={tagInput}
                            onChange={e => setTagInput(e.target.value)}
                            onKeyDown={handleAddTag}
                            InputProps={{ sx: fieldSx }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                <Button
                    variant="outlined"
                    onClick={() => handleSave(false)}
                    disabled={saving || !form.expense_date || (!form.is_mileage && !parseFloat(form.amount))}
                    sx={{
                        fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                        borderColor: ACCENT, color: ACCENT,
                        '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' },
                    }}
                >
                    {saving ? 'Saving...' : 'Save Draft'}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => handleSave(true)}
                    disabled={saving || !form.expense_date || (!form.is_mileage && !parseFloat(form.amount))}
                    sx={{
                        fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                        bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' },
                    }}
                >
                    {saving ? 'Saving...' : 'Save & Submit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
